import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Download, Help } from '@insurancemenu/imenu-components-lib/dist/icons';
import { Button, colors, Tooltip, useWindowDimensions } from '@insurancemenu/imenu-components-lib';
import StepHeader from 'components/common/headers/StepHeader';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { useHistory } from 'react-router-dom';
import { useGeneratePDFStore } from 'store/pages/rfq/rfq-template/generate-pdf/pageStore';
import { observer } from 'mobx-react-lite';
import FormSpinner from 'components/common/loaders/FormSpinner';
import useRfqTemplate from 'global/hooks/useRfqTemplate';
import clsx from 'clsx';
import { breakpointsValues } from '@insurancemenu/imenu-components-lib/dist/utils/breakpoints';
import { createStyles } from './styles';
import RFQAttributesSelectionList from './RFQAttributesSelectionList';
import {
  COMPLETE_GENERATE_PDF_STEP_TEST_ID,
  COMPLETE_GENERATE_PDF_STEP_BTN_TEST_ID,
  DOWNLOAD_GENERATED_PDF_BTN_TEST_ID,
  GENERATE_PDF_BACK_BTN_TEST_ID,
  GENERATE_PDF_COMPLETE_STEP_LOADING_TEST_ID,
  REGENERATE_PDF_GUIDELINES_TEST_ID,
  REVIEW_GENERATED_PDF_STEP_TEST_ID,
  SELECT_AND_GENERATE_PDF_STEP_TEST_ID,
  TOGGLE_ATTRIBUTES_SECTION_BTN_TEST_ID,
} from '../constants';

interface StepBlockProps {
  title: string;
  index: string;
  subtitle: string | JSX.Element;
  content: JSX.Element;
  guidelines?: JSX.Element;
  additionalHeaderElement?: JSX.Element;
  pinHeader?: boolean;
  isSectionOpen?: boolean;
  'data-test-id'?: string;
}

type GenerateStep = 'REVIEW' | 'SELECT_AND_GENERATE' | 'COMPLETE';

const StepBlock = (props: StepBlockProps): JSX.Element => {
  const { title, content, subtitle, index, guidelines, pinHeader, additionalHeaderElement, isSectionOpen } = props;
  const classes = createStyles(isSectionOpen);

  return (
    <div className={classes.stepWrapper} data-test-id={props['data-test-id']}>
      <div className={clsx(classes.stepHeader, pinHeader ? classes.pinnedHeader : '')}>
        {guidelines && guidelines}
        <StepHeader stepTitle={title} stepSubtitle={subtitle} stepNumber={index} />
        {additionalHeaderElement}
      </div>
      {content}
    </div>
  );
};

const RegeneratePdfGuidelines = (): JSX.Element => {
  const classes = createStyles();
  return (
    <div className={classes.guidelinesWrapper} data-test-id={REGENERATE_PDF_GUIDELINES_TEST_ID}>
      <Tooltip
        alignment={AlignmentTypes.BOTTOM_END}
        label="Skip this step to proceed with the current PDF without changes."
      >
        <span className={classes.guidelinesMessage}>
          <Help width="2rem" height="2rem" color={colors.green[600]} />
          Optional
        </span>
      </Tooltip>
    </div>
  );
};

const RFQGeneratePdf = observer((): JSX.Element => {
  const classes = createStyles();
  // Common data
  const { templateId, pageType } = useRfqTemplate();
  const history = useHistory();

  // Mobx store
  const {
    isPdfGenerated,
    isStepCompleted,
    isTemplateInProgress,
    downloadLink,
    isCompleteStepLoading,
    planAttributesMap,
    getInitialData,
    completeStep,
    resetInitialData,
  } = useGeneratePDFStore();

  const [showAllAttributes, setShowAllAttributes] = useState(false);

  const { width: windowWidth } = useWindowDimensions();
  const isMobileView = windowWidth <= breakpointsValues.xl;

  useEffect(() => {
    void getInitialData(templateId);
    return resetInitialData;
  }, []);

  useEffect(() => {
    setShowAllAttributes(!isPdfGenerated);
  }, [isPdfGenerated]);

  const returnBack = (): void => {
    history.push(`/product-template-checklist/${templateId}?type=${pageType}`);
  };

  const onSubmit = async (): Promise<void> => {
    await completeStep();
    returnBack();
  };

  const onDownloadPdf = (): void => {
    if (downloadLink) window.open(downloadLink, '_blank');
  };

  const toggleAttributesSection = (): void => {
    setShowAllAttributes(!showAllAttributes);
  };

  // Concomitant data
  const StepsContent: { [key in GenerateStep]: StepBlockProps } = Object.freeze({
    REVIEW: {
      title: 'Review Generated PDF',
      subtitle: (
        <p className={classes.rfqStepSubtitle}>
          You have already generated a PDF. If no changes are needed, you can complete this step. Otherwise, download
          and check the file before proceeding
        </p>
      ),
      content: (
        <Button
          color="primary"
          leftIconProp={<Download />}
          size="large"
          onClick={onDownloadPdf}
          data-test-id={DOWNLOAD_GENERATED_PDF_BTN_TEST_ID}
        >
          Download PDF
        </Button>
      ),
      index: '1',
      'data-test-id': REVIEW_GENERATED_PDF_STEP_TEST_ID,
    },
    SELECT_AND_GENERATE: {
      title: isPdfGenerated ? 'Select Attributes and Regenerate PDF' : 'Select Attributes and Generate PDF',
      subtitle: (
        <p className={classes.rfqStepSubtitle}>
          Choose the attributes to include in the file. Use the toggle to automatically select all covered attributes or
          manually select using checkboxes. If you update this step, you will need to regenerate the PDF.
        </p>
      ),
      content: <RFQAttributesSelectionList showAllAttributes={showAllAttributes} />,
      index: isPdfGenerated ? '2' : '1',
      guidelines: isPdfGenerated && isTemplateInProgress && !isMobileView ? <RegeneratePdfGuidelines /> : undefined,
      pinHeader: Object.keys(planAttributesMap).length > 0 && !isMobileView,
      isSectionOpen: showAllAttributes,
      additionalHeaderElement: (
        <Button
          type="text"
          color="primary"
          className={classes.attributesSectionButton}
          rightIconProp={showAllAttributes ? <ChevronUp /> : <ChevronDown />}
          onClick={toggleAttributesSection}
          data-test-id={TOGGLE_ATTRIBUTES_SECTION_BTN_TEST_ID}
        >
          {showAllAttributes ? 'Hide All attributes' : 'Show All attributes'}
        </Button>
      ),
      'data-test-id': SELECT_AND_GENERATE_PDF_STEP_TEST_ID,
    },
    COMPLETE: {
      title: 'Complete Step',
      subtitle: (
        <p className={classes.wideRfqStepSubtitle}>
          Continue with the checklist using the generated PDF file. <br />
          Ensure all changes are included before finalizing
        </p>
      ),
      content: (
        <>
          <FormSpinner display={isCompleteStepLoading} data-test-id={GENERATE_PDF_COMPLETE_STEP_LOADING_TEST_ID} />
          <Button
            color="custom"
            customColor={colors.green[600]}
            size="large"
            disabled={!isPdfGenerated}
            onClick={onSubmit}
            data-test-id={COMPLETE_GENERATE_PDF_STEP_BTN_TEST_ID}
          >
            {isStepCompleted ? 'Update' : 'Complete'}
          </Button>
        </>
      ),
      index: isPdfGenerated ? '3' : '2',
      'data-test-id': COMPLETE_GENERATE_PDF_STEP_TEST_ID,
    },
  });

  return (
    <>
      <div className={classes.pdfSteps}>
        {isPdfGenerated && <StepBlock {...StepsContent.REVIEW} />}
        <StepBlock {...StepsContent.SELECT_AND_GENERATE} />
        {isTemplateInProgress && <StepBlock {...StepsContent.COMPLETE} />}
      </div>
      <footer className={classes.footer}>
        <Button type="outlined" size="large" onClick={returnBack} data-test-id={GENERATE_PDF_BACK_BTN_TEST_ID}>
          Back
        </Button>
      </footer>
    </>
  );
});

export default RFQGeneratePdf;
